import Button from '@/components/Buttons/Button';
import IconButton from '@/components/Buttons/IconButton';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import ArrowDownIcon from '@/images/arrows/ArrowDown.svg';
import XIcon from '@/images/Close.svg';
import DialogIcon from '@/images/Dialog.svg';
import { useGetConversationDetails } from '@/pages/agents/api/insights/useGetConversationDetails';
import theme from '@/theme';
import styled from '@emotion/styled';
import { useRef } from 'react';
import { useAgentInsightsContext } from '../../contex/useAgentInsightsContext';
import { ConversationItem } from './ConversationItem';
import { ConversationItemSkeleton } from './ConversationItemSkeleton';

const DrawerBackdrop = styled.div<{ isOpen: boolean }>`
    position: fixed;
    top: 0;
    right: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.25);
    z-index: 20;
    transition:
        opacity 0.3s ease-in-out,
        visibility 0.3s ease-in-out;
    opacity: ${props => (props.isOpen ? 1 : 0)};
    visibility: ${props => (props.isOpen ? 'visible' : 'hidden')};
`;

const DrawerContainer = styled.div<{ isOpen: boolean }>`
    position: fixed;
    display: flex;
    flex-direction: column;
    top: 0;
    right: ${props => (props.isOpen ? '0px' : '-40%')};
    width: 40%;
    height: 100%;
    background-color: #fff;
    transition: right 0.3s ease-in-out;
`;

const DrawerHeader = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 24px 32px 16px;

    color: ${theme.colors.secondary[900]};
    font-size: 16px;
    font-weight: 600;
    border-bottom: 1px solid ${theme.colors.black[300]};
`;

const ConversationDetails = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    padding: 16px 32px 32px;
    overflow-y: auto;
`;

const UserQuery = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    padding: 8px;
    width: fit-content;

    border-radius: 8px;
    border: 1px solid ${theme.colors.black[300]};

    color: ${theme.colors.secondary[900]};
    text-align: center;
    font-size: 12px;
    font-weight: 400;
`;

const Conversation = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 48px;
    gap: 12px;
    flex: 1;
    background-color: ${theme.colors.black[50]};
    margin-top: 16px;
    margin-bottom: 8px;
    border-radius: 10px;
    overflow-y: auto;
`;

const BackToTopButton = styled(Button)`
    display: flex;
    align-items: center;
    gap: 8px;
    margin-left: auto;

    &.MuiButtonBase-root {
        background-color: ${theme.colors.white};
        border: 1px solid ${theme.colors.black[300]};

        &:hover {
            background-color: ${theme.colors.black[50]} !important;
        }
    }
`;

interface Props {
    groupName: string;
    conversationId: string | null;
    onClose: () => void;
}

export const ConversationDrawer = ({ groupName, conversationId, onClose }: Props) => {
    const { isPublic, shareId } = useAgentInsightsContext();
    const { data, isLoading } = useGetConversationDetails({ conversationId, isPublic, shareId });
    const conversationRef = useRef<HTMLDivElement>(null);

    const handleScrollToTop = () => {
        if (conversationRef.current) {
            conversationRef.current.scrollTo({ top: 0, behavior: 'smooth' });
        }
    };

    const conversation = data?.conversation;
    const sortedConversation = conversation?.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

    return (
        <DrawerBackdrop onClick={onClose} isOpen={!!conversationId}>
            <DrawerContainer isOpen={!!conversationId} onClick={e => e.stopPropagation()}>
                <DrawerHeader>
                    Conversation
                    <IconButton ariaLabel="close" onClick={onClose}>
                        <MaskedIcon src={XIcon} size="16px" color={theme.colors.black[900]} />
                    </IconButton>
                </DrawerHeader>

                <ConversationDetails>
                    <UserQuery>
                        <MaskedIcon src={DialogIcon} size="16px" color={theme.colors.black[900]} />
                        {groupName}
                    </UserQuery>
                    <Conversation ref={conversationRef}>
                        {isLoading ? (
                            <ConversationItemSkeleton />
                        ) : (
                            sortedConversation?.map(entry => (
                                <ConversationItem key={`${entry.timestamp}-${entry.input.substring(0, 10)}`} entry={entry} />
                            ))
                        )}
                    </Conversation>

                    <BackToTopButton onClick={handleScrollToTop}>
                        Back to top
                        <MaskedIcon src={ArrowDownIcon} size="16px" color={theme.colors.black[900]} />
                    </BackToTopButton>
                </ConversationDetails>
            </DrawerContainer>
        </DrawerBackdrop>
    );
};
